import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  User, 
  Calendar, 
  Clock, 
  FileText, 
  ShieldCheck, 
  Camera, 
  DollarSign, 
  ArrowLeft, 
  Save, 
  Printer, 
  PenTool, 
  CheckCircle2, 
  Receipt as ReceiptIcon,
  Zap,
  MapPin,
  ListOrdered,
  Search,
  Trash2,
  Phone,
  Car,
  Wind,
  Plus,
  AlertCircle,
  Smartphone
} from 'lucide-react';
import { 
  WorkOrder, 
  WorkOrderStatus,
  NicheKey, 
  ChecklistItem, 
  WorkOrderPhoto, 
  DigitalSignature, 
  VehicleData, 
  DeviceData,
  HvacEquipmentData, 
  BeautyTechnicalCard,
  InsuranceData 
} from '../types';
import { getLocalDateString } from '../utils/calendarUtils';
import { NICHE_PRESETS } from '../constants/nichePresets';
import NicheQuickSelectors, { SelectedNicheItem } from './NicheQuickSelectors';
import WorkOrderPhotoUploader from './WorkOrderPhotoUploader';
import TouchSignatureModal from './TouchSignatureModal';
import ZeroDigitationWidget from './work-order/ZeroDigitationWidget';
import DeviceDataWidget from './work-order/DeviceDataWidget';
import TechnicalChecklistSection from './work-order/TechnicalChecklistSection';
import WorkOrderPdfPreviewModal from './work-order/WorkOrderPdfPreviewModal';
import NfseEmissionModal from './NfseEmissionModal';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import { withoutDevicePasscode } from '../utils/deviceDataUtils';
import { fetchAddressByCep } from '../services/publicDataServices';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';
import { useTeamPermissions } from '../../../contexts/TeamPermissionsContext';
import { consumeWorkOrderDraft, BRIDGE_EVENTS } from '../utils/commandBridge';
import { supabase } from '../../../lib/supabase';

interface WorkOrderCreatorProps {
  workOrders?: WorkOrder[];
  initialWorkOrder?: Partial<WorkOrder>;
  nicheKey?: NicheKey;
  providerName: string;
  onSave: (workOrder: WorkOrder) => Promise<void> | void;
  onDeleteWorkOrder?: (id: string) => Promise<void> | void;
  onBack: () => void;
  onOpenReceipt?: (workOrder: WorkOrder) => void;
  onExportPDF?: (workOrder: WorkOrder) => void;
}

export default function WorkOrderCreator({
  workOrders = [],
  initialWorkOrder,
  nicheKey = 'eletricista',
  providerName,
  onSave,
  onDeleteWorkOrder,
  onBack,
  onOpenReceipt,
  onExportPDF
}: WorkOrderCreatorProps) {
  const preset = NICHE_PRESETS[nicheKey] || NICHE_PRESETS.eletricista;

  const { can } = useTeamPermissions();
  const canDelete = can('delete_records');

  const [activeTab, setActiveTab] = useState<'create' | 'history'>('create');
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<'all' | WorkOrderStatus>('all');

  const [currentWorkOrderId, setCurrentWorkOrderId] = useState(initialWorkOrder?.id || '');
  const [selectedNiche, setSelectedNiche] = useState<NicheKey>(initialWorkOrder?.nicheKey || nicheKey);
  const [orderStatus, setOrderStatus] = useState<WorkOrderStatus>(initialWorkOrder?.status || 'open');
  const [orderNumber, setOrderNumber] = useState(initialWorkOrder?.orderNumber || `OS-${Date.now().toString().slice(-4)}`);
  const [title, setTitle] = useState(initialWorkOrder?.title || `Ordem de Serviço - ${preset.displayName}`);
  const [clientName, setClientName] = useState(initialWorkOrder?.clientName || '');
  const [clientPhone, setClientPhone] = useState(initialWorkOrder?.clientPhone || '');
  const [clientAddress, setClientAddress] = useState(initialWorkOrder?.clientAddress || '');
  const [clientDocument, setClientDocument] = useState(initialWorkOrder?.clientDocument || '');
  const [scheduledStart, setScheduledStart] = useState(initialWorkOrder?.scheduledStart || getLocalDateString());
  const [scheduledEnd, setScheduledEnd] = useState(initialWorkOrder?.scheduledEnd || '');
  const [paymentTerms, setPaymentTerms] = useState(initialWorkOrder?.paymentTerms || preset.defaultPaymentTerms);
  const [warrantyTerms, setWarrantyTerms] = useState(initialWorkOrder?.warrantyTerms || preset.defaultWarrantyTerms);
  const [technicalReport, setTechnicalReport] = useState(initialWorkOrder?.technicalReport || '');
  const [notes, setNotes] = useState(initialWorkOrder?.notes || '');
  
  // Metadados Técnicos Especialistas
  const [vehicleData, setVehicleData] = useState<VehicleData | undefined>(initialWorkOrder?.vehicleData);
  const [deviceData, setDeviceData] = useState<DeviceData | undefined>(initialWorkOrder?.deviceData);
  const [hvacData, setHvacData] = useState<HvacEquipmentData | undefined>(initialWorkOrder?.hvacData);
  const [beautyData, setBeautyData] = useState<BeautyTechnicalCard | undefined>(initialWorkOrder?.beautyData);
  const [insuranceData, setInsuranceData] = useState<InsuranceData | undefined>(initialWorkOrder?.insuranceData);

  // Busca rápida de CEP no endereço
  const [cepInput, setCepInput] = useState('');
  const [isSearchingCep, setIsSearchingCep] = useState(false);

  const [scopeItems, setScopeItems] = useState<SelectedNicheItem[]>(() => {
    if (initialWorkOrder?.itemsBreakdown && initialWorkOrder.itemsBreakdown.length > 0) {
      return initialWorkOrder.itemsBreakdown.map((item, idx) => ({
        id: `item_${idx}`,
        category: item.category || 'Serviços',
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total,
        isMaterial: item.isMaterial
      }));
    }
    return [];
  });
  const [photos, setPhotos] = useState<WorkOrderPhoto[]>(initialWorkOrder?.photos || []);
  const [signatures, setSignatures] = useState<DigitalSignature[]>(initialWorkOrder?.signatures || []);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [checklists, setChecklists] = useState<ChecklistItem[]>(() => {
    if (initialWorkOrder?.checklistData && initialWorkOrder.checklistData.length > 0) {
      return initialWorkOrder.checklistData;
    }
    return preset.defaultChecklists.map((label, idx) => ({
      id: `check_${idx}`,
      label,
      checked: false
    }));
  });

  const maskPhone = (val: string) => {
    let digits = val.replace(/\D/g, '');
    if (digits.startsWith('0') && (digits.length === 11 || digits.length === 12)) digits = digits.slice(1);
    if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
    if (digits.length > 11) digits = digits.slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const maskDocument = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    if (digits.length <= 11) {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
      if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
    }
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const maskCep = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const [previewWorkOrder, setPreviewWorkOrder] = useState<Partial<WorkOrder> | null>(null);
  const [selectedWoForNfse, setSelectedWoForNfse] = useState<WorkOrder | null>(null);

  const generateUuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Ponte Omnibar -> Ordens de Serviço: consome o rascunho de OS e preenche os campos
  useEffect(() => {
    const checkDraft = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'guest';
        const draft = consumeWorkOrderDraft(userId);
        if (draft) {
          setActiveTab('create');
          if (draft.title) setTitle(draft.title);
          if (draft.clientName) setClientName(draft.clientName);
          if (draft.clientPhone) setClientPhone(maskPhone(draft.clientPhone));
          if (draft.clientAddress) setClientAddress(draft.clientAddress);
          if (draft.clientDocument) setClientDocument(maskDocument(draft.clientDocument));
          if (draft.problemDescription) setTechnicalReport(draft.problemDescription);
          if (draft.notes) setNotes(`[Comando] ${draft.notes}`);
          if (draft.deviceInfo) {
            setDeviceData(prev => ({
              deviceType: prev?.deviceType || 'outro',
              brand: prev?.brand || '',
              model: draft.deviceInfo || '',
              hasPasscode: prev?.hasPasscode ?? false,
              ...(prev || {})
            }));
          }
        }
      } catch (err) {
        console.warn('[WorkOrderCreator] Erro ao consumir rascunho de OS:', err);
      }
    };

    checkDraft();
    window.addEventListener(BRIDGE_EVENTS.work_orders, checkDraft);
    return () => window.removeEventListener(BRIDGE_EVENTS.work_orders, checkDraft);
  }, []);

  useEffect(() => {
    const currentPreset = NICHE_PRESETS[selectedNiche] || NICHE_PRESETS.eletricista;
    if (!initialWorkOrder?.checklistData || initialWorkOrder.checklistData.length === 0) {
      setChecklists(currentPreset.defaultChecklists.map((label, idx) => ({
        id: `check_${idx}`,
        label,
        checked: false
      })));
    }
    if (!initialWorkOrder?.warrantyTerms) {
      setWarrantyTerms(currentPreset.defaultWarrantyTerms);
    }
    if (!initialWorkOrder?.paymentTerms) {
      setPaymentTerms(currentPreset.defaultPaymentTerms);
    }
  }, [selectedNiche, initialWorkOrder]);

  const handleSearchCep = async () => {
    if (!cepInput.trim()) return;
    setIsSearchingCep(true);
    const result = await fetchAddressByCep(cepInput);
    setIsSearchingCep(false);
    if (result.success) {
      setClientAddress(result.formattedAddress);
    }
  };

  const handleSaveSignature = (sig: DigitalSignature) => {
    setSignatures([...signatures, sig]);
  };

  const totalLabor = scopeItems.filter(i => !i.isMaterial).reduce((acc, curr) => acc + curr.total, 0);
  const totalMaterials = scopeItems.filter(i => i.isMaterial).reduce((acc, curr) => acc + curr.total, 0);
  const finalPrice = totalLabor + totalMaterials || initialWorkOrder?.finalPrice || 0;

  const handleDirectWhatsAppShare = (wo: WorkOrder) => {
    const text = encodeURIComponent(
      `Olá, *${wo.clientName || 'Cliente'}*! 👋 Segue o resumo da sua *Ordem de Serviço Nº ${wo.orderNumber}* ("${wo.title || 'Serviço'}").\n\n` +
      (wo.deviceData ? `📱 *Aparelho:* ${wo.deviceData.brand} ${wo.deviceData.model}\n` : '') +
      `💰 *Valor Total:* ${formatCurrencyBRL(wo.finalPrice || 0)}\n` +
      `📅 *Data:* ${wo.createdAt ? getLocalDateString(new Date(wo.createdAt)) : getLocalDateString()}\n` +
      (wo.warrantyTerms ? `🛡️ *Garantia:* Confira os prazos no documento impresso.\n` : '') +
      `\nFicamos à total disposição!`
    );
    if (wo.clientPhone) {
      const cleanPhone = wo.clientPhone.replace(/\D/g, '');
      const finalPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
      window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${text}`, '_blank');
    } else {
      window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
    }
  };

  const handleSelectForEdit = (wo: WorkOrder) => {
    setCurrentWorkOrderId(wo.id);
    setSelectedNiche(wo.nicheKey || 'eletricista');
    setOrderStatus(wo.status || 'open');
    setOrderNumber(wo.orderNumber);
    setTitle(wo.title);
    setClientName(wo.clientName);
    setClientPhone(wo.clientPhone || '');
    setClientAddress(wo.clientAddress || '');
    setClientDocument(wo.clientDocument || '');
    setScheduledStart(wo.scheduledStart || getLocalDateString());
    setScheduledEnd(wo.scheduledEnd || '');
    setPaymentTerms(wo.paymentTerms || '');
    setWarrantyTerms(wo.warrantyTerms || '');
    setTechnicalReport(wo.technicalReport || '');
    setNotes(wo.notes || '');
    setVehicleData(wo.vehicleData);
    setDeviceData(wo.deviceData);
    setHvacData(wo.hvacData);
    setBeautyData(wo.beautyData);
    setInsuranceData(wo.insuranceData);
    setPhotos(wo.photos || []);
    setSignatures(wo.signatures || []);
    if (wo.checklistData && wo.checklistData.length > 0) {
      setChecklists(wo.checklistData);
    }
    if (wo.itemsBreakdown && wo.itemsBreakdown.length > 0) {
      setScopeItems(wo.itemsBreakdown.map((item, idx) => ({
        id: `item_${idx}`,
        category: item.category || 'Serviços',
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total,
        isMaterial: item.isMaterial
      })));
    } else {
      setScopeItems([]);
    }
    setActiveTab('create');
  };

  const handleResetForm = () => {
    setCurrentWorkOrderId('');
    const currentPreset = NICHE_PRESETS[selectedNiche] || NICHE_PRESETS.eletricista;
    setOrderNumber(`OS-${Date.now().toString().slice(-4)}`);
    setTitle(`Ordem de Serviço - ${currentPreset.displayName}`);
    setOrderStatus('open');
    setClientName('');
    setClientPhone('');
    setClientAddress('');
    setClientDocument('');
    setScheduledStart(getLocalDateString());
    setScheduledEnd('');
    setPaymentTerms(currentPreset.defaultPaymentTerms);
    setWarrantyTerms(currentPreset.defaultWarrantyTerms);
    setTechnicalReport('');
    setNotes('');
    setVehicleData(undefined);
    setDeviceData(undefined);
    setHvacData(undefined);
    setBeautyData(undefined);
    setInsuranceData(undefined);
    setPhotos([]);
    setSignatures([]);
    setScopeItems([]);
    setChecklists(currentPreset.defaultChecklists.map((label, idx) => ({
      id: `check_${idx}`,
      label,
      checked: false
    })));
    setActiveTab('create');
  };

  const handleDeleteWorkOrderAction = async (wo: WorkOrder) => {
    if (!onDeleteWorkOrder) return;
    const confirmDelete = window.confirm(`Tem certeza que deseja excluir a OS Nº ${wo.orderNumber} (${wo.clientName})?\nEsta ação não poderá ser desfeita.`);
    if (!confirmDelete) return;

    try {
      await onDeleteWorkOrder(wo.id);
      if (wo.id === currentWorkOrderId) {
        handleResetForm();
      }
    } catch (err) {
      console.error('Erro ao excluir OS:', err);
      alert('Erro ao excluir a Ordem de Serviço.');
    }
  };

  const filteredWorkOrders = workOrders.filter(wo => {
    const term = historySearch.toLowerCase().trim();
    const matchesSearch = !term || (
      wo.orderNumber.toLowerCase().includes(term) ||
      wo.clientName.toLowerCase().includes(term) ||
      wo.title.toLowerCase().includes(term) ||
      (wo.clientDocument && wo.clientDocument.includes(term)) ||
      (wo.vehicleData?.plate && wo.vehicleData.plate.toLowerCase().includes(term))
    );

    const matchesStatus = historyStatusFilter === 'all' || wo.status === historyStatusFilter;

    return matchesSearch && matchesStatus;
  });

  const totalCompletedAmount = workOrders
    .filter(w => w.status === 'completed')
    .reduce((acc, w) => acc + (Number(w.finalPrice) || 0), 0);

  const totalOpenCount = workOrders.filter(w => w.status === 'open' || w.status === 'in_progress').length;

  const handleSaveOrder = async () => {
    if (!clientName.trim()) {
      alert('Por favor, informe o nome do cliente.');
      return;
    }

    setIsSaving(true);
    try {
      const compiledWorkOrder: WorkOrder = {
        id: currentWorkOrderId || initialWorkOrder?.id || generateUuid(),
        userId: initialWorkOrder?.userId || 'current_user',
        quoteId: initialWorkOrder?.quoteId,
        orderNumber,
        title,
        nicheKey: selectedNiche,
        status: orderStatus,
        clientName,
        clientPhone,
        clientAddress,
        clientDocument,
        scheduledStart,
        scheduledEnd,
        completedAt: orderStatus === 'completed' ? new Date().toISOString() : undefined,
        checklistData: checklists,
        itemsBreakdown: scopeItems.map(item => ({
          category: item.category,
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.total,
          isMaterial: item.isMaterial
        })),
        technicalReport,
        warrantyTerms,
        totalLabor,
        totalMaterials,
        finalPrice,
        paymentTerms,
        notes,
        photos,
        signatures,
        deviceData: withoutDevicePasscode(deviceData),
        vehicleData,
        hvacData,
        beautyData,
        insuranceData,
        createdAt: initialWorkOrder?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onSave(compiledWorkOrder);
      setDeviceData(withoutDevicePasscode(deviceData));
      if (!currentWorkOrderId) {
        setCurrentWorkOrderId(compiledWorkOrder.id);
      }
    } catch (error) {
      console.error('Erro ao salvar Ordem de Serviço:', error);
      alert('Ocorreu um erro ao salvar a Ordem de Serviço.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Sticky Slim (56px) - Padrão R-MAX Mobile */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 sm:px-6 h-14 flex items-center shadow-2xs w-full">
        <div className="max-w-5xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
              title="Voltar ao Painel"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
                {activeTab === 'create' ? (currentWorkOrderId ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço') : 'Histórico de Ordens (OS)'}
              </h1>
              <p className="text-[11px] text-zinc-500 truncate font-medium">
                {activeTab === 'create' ? `${orderNumber} • ${preset.displayName}` : `${workOrders.length} ordens cadastradas`}
              </p>
            </div>
          </div>

          {/* Ações Rápidas no Header */}
          <div className="flex items-center gap-1.5 shrink-0">
            <ScreenTourButton screenKey="work_order" />
            {activeTab === 'create' ? (
              <>
                <button
                  type="button"
                  onClick={() => setPreviewWorkOrder({
                    id: currentWorkOrderId || initialWorkOrder?.id || `wo_${Date.now()}`,
                    userId: 'current_user',
                    orderNumber,
                    title,
                    nicheKey: selectedNiche,
                    status: orderStatus,
                    clientName,
                    clientPhone,
                    clientAddress,
                    clientDocument,
                    checklistData: checklists,
                    itemsBreakdown: scopeItems,
                    technicalReport,
                    warrantyTerms,
                    totalLabor,
                    totalMaterials,
                    finalPrice,
                    paymentTerms,
                    photos,
                    signatures,
                    deviceData,
                    vehicleData,
                    hvacData,
                    beautyData,
                    insuranceData,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  })}
                  className="p-2 sm:px-3 sm:py-2 bg-emerald-50 hover:bg-emerald-100 text-brand border border-emerald-200/80 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-2xs"
                  title="Visualizar e Exportar PDF Real"
                >
                  <Printer className="h-4 w-4" />
                  <span className="hidden sm:inline font-bold">Ver PDF</span>
                </button>

                <button
                  type="button"
                  onClick={handleSaveOrder}
                  disabled={isSaving}
                  className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm shadow-brand/25 flex items-center gap-1.5 cursor-pointer active:scale-95 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  <span>{isSaving ? 'Salvando...' : 'Salvar OS'}</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleResetForm}
                className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>Nova OS</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Abas de Navegação */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4">
        <div className="flex bg-zinc-200/70 p-1 rounded-2xl gap-1 text-xs font-bold">
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            className={`flex-1 py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'create'
                ? 'bg-white text-zinc-900 shadow-xs font-black'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            <ClipboardList className="h-4 w-4" />
            <span>{currentWorkOrderId ? 'Editando OS' : 'Emissão de OS'}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'history'
                ? 'bg-white text-zinc-900 shadow-xs font-black'
                : 'text-zinc-600 hover:text-zinc-900'
            }`}
          >
            <ListOrdered className="h-4 w-4" />
            <span>Ordens de Serviço ({workOrders.length})</span>
          </button>
        </div>
      </div>

      {/* ─── ABA 1: EMISSÃO / EDIÇÃO DA OS ─── */}
      {activeTab === 'create' && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 space-y-5">
          
          {/* Seletor de Status da OS & Modo Edição */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-4 sm:p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-emerald-50 text-brand flex items-center justify-center font-bold shrink-0">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Status da Ordem</span>
                  <span className="text-xs font-black text-zinc-800">Situação Atual do Serviço</span>
                </div>
              </div>

              {currentWorkOrderId && (
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer active:scale-95"
                >
                  Nova OS
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full pt-1">
              {[
                { status: 'open' as WorkOrderStatus, label: 'Aberta', color: 'border-blue-200 text-blue-700 bg-blue-50' },
                { status: 'in_progress' as WorkOrderStatus, label: 'Em Execução', color: 'border-amber-200 text-amber-700 bg-amber-50' },
                { status: 'completed' as WorkOrderStatus, label: 'Concluída', color: 'border-emerald-200 text-emerald-800 bg-emerald-50' },
                { status: 'canceled' as WorkOrderStatus, label: 'Cancelada', color: 'border-zinc-200 text-zinc-600 bg-zinc-100' }
              ].map(st => (
                <button
                  key={st.status}
                  type="button"
                  onClick={() => setOrderStatus(st.status)}
                  className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition-all cursor-pointer text-center active:scale-95 ${
                    orderStatus === st.status
                      ? `${st.color} ring-2 ring-brand/30 shadow-xs font-black`
                      : 'border-zinc-200/80 text-zinc-600 hover:bg-zinc-50'
                  }`}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          {/* 1. Widget de Zero Digitação */}
          <ZeroDigitationWidget
            selectedNiche={selectedNiche}
            vehicleData={vehicleData}
            setVehicleData={setVehicleData}
            hvacData={hvacData}
            setHvacData={setHvacData}
            setTitle={setTitle}
            setClientName={setClientName}
            setClientDocument={setClientDocument}
            setClientAddress={setClientAddress}
            setClientPhone={setClientPhone}
            setTechnicalReport={setTechnicalReport}
          />

          {selectedNiche === 'assistencia_celular' && (
            <DeviceDataWidget
              deviceData={deviceData}
              onChange={setDeviceData}
            />
          )}

          {/* 2. Dados Principais & Cliente */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-zinc-100 pb-3">
              <User className="h-5 w-5 text-brand" />
              <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Dados do Cliente & Serviço</h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Nome do Cliente *</label>
                <input
                  type="text"
                  placeholder="Ex: Lucas Almeida ou Auto Mecânica Silva"
                  value={clientName}
                  onChange={e => setClientName(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">WhatsApp / Telefone</label>
                <input
                  type="text"
                  placeholder="(11) 99999-9999"
                  value={clientPhone}
                  onChange={e => setClientPhone(maskPhone(e.target.value))}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">CPF / CNPJ</label>
                <input
                  type="text"
                  placeholder="000.000.000-00 ou 00.000.000/0001-00"
                  value={clientDocument}
                  onChange={e => setClientDocument(maskDocument(e.target.value))}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 font-mono"
                />
              </div>

              {/* Endereço com busca de CEP */}
              <div className="sm:col-span-2 space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Endereço da Execução</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="text"
                      placeholder="CEP: 00000-000"
                      value={cepInput}
                      onChange={e => setCepInput(maskCep(e.target.value))}
                      className="w-24 bg-zinc-50 border border-zinc-100 rounded-lg px-2 py-0.5 text-[10px] font-mono focus:outline-none focus:ring-1 focus:ring-brand/20"
                    />
                    <button
                      type="button"
                      onClick={handleSearchCep}
                      disabled={isSearchingCep}
                      className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-brand font-bold text-[10px] rounded-lg border border-emerald-100 cursor-pointer active:scale-95"
                    >
                      {isSearchingCep ? '...' : 'Puxar CEP'}
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Rua, Número, Bairro, Cidade - UF"
                  value={clientAddress}
                  onChange={e => setClientAddress(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Data de Início</label>
                <input
                  type="date"
                  value={scheduledStart}
                  onChange={e => setScheduledStart(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>
            </div>
          </div>

          {/* 2.5 Dados da Seguradora & Sinistro (Para Serviços Automotivos / Técnicos com Seguro) */}
          {(selectedNiche === 'mecanica' || insuranceData) && (
            <div className="bg-white border border-purple-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-purple-50 pb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold shrink-0">
                    <ShieldCheck className="h-4 w-4" />
                  </div>
                  <div>
                    <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">
                      Dados da Seguradora & Sinistro
                    </h2>
                    <span className="text-[10px] text-zinc-400">
                      Preencha caso o atendimento seja coberto por seguradora ou terceiro
                    </span>
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer bg-purple-50/70 border border-purple-200/80 px-3 py-1.5 rounded-xl">
                  <input
                    type="checkbox"
                    checked={!!insuranceData}
                    onChange={e => {
                      if (e.target.checked) {
                        setInsuranceData({ insuranceCompany: '', claimNumber: '', policyNumber: '', deductibleAmount: 0, claimApproved: false });
                      } else {
                        setInsuranceData(undefined);
                      }
                    }}
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-zinc-300"
                  />
                  <span className="text-xs font-black text-purple-950">Atendimento com Seguro</span>
                </label>
              </div>

              {insuranceData && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-1">
                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Companhia de Seguro</label>
                    <input
                      type="text"
                      placeholder="Ex: Porto Seguro, Azul, Tokio Marine, Bradesco"
                      value={insuranceData.insuranceCompany || ''}
                      onChange={e => setInsuranceData({ ...insuranceData, insuranceCompany: e.target.value })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Número do Sinistro</label>
                    <input
                      type="text"
                      placeholder="Ex: 53120260903001"
                      value={insuranceData.claimNumber || ''}
                      onChange={e => setInsuranceData({ ...insuranceData, claimNumber: e.target.value })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-bold font-mono placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Valor da Franquia (R$)</label>
                    <input
                      type="number"
                      placeholder="0,00"
                      value={insuranceData.deductibleAmount || ''}
                      onChange={e => setInsuranceData({ ...insuranceData, deductibleAmount: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-bold font-mono placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Vistoriador / Perito</label>
                    <input
                      type="text"
                      placeholder="Nome do perito da seguradora"
                      value={insuranceData.surveyorName || ''}
                      onChange={e => setInsuranceData({ ...insuranceData, surveyorName: e.target.value })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Número da Apólice</label>
                    <input
                      type="text"
                      placeholder="Número da apólice do segurado"
                      value={insuranceData.policyNumber || ''}
                      onChange={e => setInsuranceData({ ...insuranceData, policyNumber: e.target.value })}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                    />
                  </div>

                  <div className="space-y-1 flex items-end">
                    <label className="flex items-center gap-2 p-2.5 bg-purple-50/60 border border-purple-200/80 rounded-xl cursor-pointer w-full">
                      <input
                        type="checkbox"
                        checked={!!insuranceData.claimApproved}
                        onChange={e => setInsuranceData({ ...insuranceData, claimApproved: e.target.checked })}
                        className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-zinc-300"
                      />
                      <span className="text-xs font-bold text-purple-950">Vistoria / Reparo Aprovado</span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 3. Seletores Rápidos do Nicho */}
          <NicheQuickSelectors
            nicheKey={selectedNiche}
            selectedItems={scopeItems}
            onItemsChange={setScopeItems}
          />

          {/* 4. Checklist Técnico de Vistoria (Modular) */}
          <TechnicalChecklistSection
            checklists={checklists}
            setChecklists={setChecklists}
          />

          {/* 5. Fotos da OS (Antes / Durante / Depois) */}
          <WorkOrderPhotoUploader
            photos={photos}
            onPhotosChange={setPhotos}
          />

          {/* 6. Garantia, Pagamento & Observações */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-zinc-100 pb-3">
              <FileText className="h-5 w-5 text-brand" />
              <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Garantia, Pagamento & Observações</h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Condições de Pagamento</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={e => setPaymentTerms(e.target.value)}
                  placeholder="Ex: Entrada 40% + 28ddl ou 50% sinal"
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Termo de Garantia</label>
                <input
                  type="text"
                  value={warrantyTerms}
                  onChange={e => setWarrantyTerms(e.target.value)}
                  placeholder="Ex: Garantia de 90 dias conforme combinado..."
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="sm:col-span-2 space-y-1">
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">Observações do Serviço e Orientações ao Cliente</label>
                <textarea
                  rows={3}
                  value={technicalReport}
                  onChange={e => setTechnicalReport(e.target.value)}
                  placeholder="Descreva como o serviço foi feito ou orientações para o cliente..."
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl p-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 resize-none font-medium"
                />
              </div>
            </div>
          </div>

          {/* 7. Assinaturas Digitais Touchscreen */}
          <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div className="flex items-center gap-2.5">
                <PenTool className="h-5 w-5 text-brand" />
                <div>
                  <h2 className="text-xs font-black uppercase tracking-wider text-zinc-900">Assinatura do Cliente na Tela</h2>
                  <p className="text-[11px] text-zinc-500">Confirmação e aceite do cliente no celular</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSignatureModalOpen(true)}
                className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-bold text-xs rounded-xl transition-all shadow-sm shadow-brand/20 cursor-pointer active:scale-95"
              >
                {signatures.length > 0 ? 'Atualizar Assinaturas' : 'Coletar Assinatura'}
              </button>
            </div>

            {signatures.length === 0 ? (
              <div className="py-6 border border-dashed border-zinc-200 rounded-2xl text-center p-4 text-xs text-zinc-400 bg-zinc-50/50">
                Nenhuma assinatura coletada ainda. Toque em "Coletar Assinatura" para colher a rubrica do cliente no celular.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {signatures.map((sig, idx) => (
                  <div key={idx} className="p-4 bg-zinc-50 border border-zinc-100 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-brand flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4" />
                        Documento Confirmado
                      </span>
                      <span className="text-[10px] text-zinc-400">
                        {new Date(sig.signedAt).toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="h-20 bg-white border border-zinc-100 rounded-xl overflow-hidden flex items-center justify-center p-2">
                      <img src={sig.clientSignatureBase64} alt="Assinatura do Cliente" className="max-h-full object-contain" />
                    </div>
                    <p className="text-[10px] text-zinc-700 truncate font-medium">
                      Assinado por: <strong>{sig.clientName}</strong>
                    </p>
                    <p className="text-[9px] text-zinc-400 font-mono truncate">
                      Hash: {sig.verificationHash}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resumo Financeiro da OS */}
          <div className="p-6 bg-white border border-zinc-100 rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-sm">
            <div>
              <span className="text-[10px] font-black text-brand uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                Total da Ordem de Serviço
              </span>
              <p className="text-2xl sm:text-3xl font-black text-zinc-900 mt-1">
                {formatCurrencyBRL(finalPrice)}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5 font-medium">
                Mão de Obra: {formatCurrencyBRL(totalLabor)} • Materiais: {formatCurrencyBRL(totalMaterials)}
              </p>
            </div>

            <div className="flex flex-wrap gap-3 w-full sm:w-auto">
              {onOpenReceipt && (
                <button
                  type="button"
                  onClick={() => onOpenReceipt({
                    id: currentWorkOrderId || initialWorkOrder?.id || `wo_${Date.now()}`,
                    userId: 'current_user',
                    orderNumber,
                    title,
                    nicheKey: selectedNiche,
                    status: orderStatus,
                    clientName,
                    clientPhone,
                    clientAddress,
                    clientDocument,
                    checklistData: checklists,
                    itemsBreakdown: scopeItems,
                    technicalReport,
                    warrantyTerms,
                    totalLabor,
                    totalMaterials,
                    finalPrice,
                    paymentTerms,
                    photos,
                    signatures,
                    vehicleData,
                    hvacData,
                    beautyData,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  })}
                  className="flex-1 sm:flex-initial px-5 py-3 bg-zinc-50 hover:bg-zinc-100 text-zinc-800 font-bold text-xs rounded-xl border border-zinc-100 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                >
                  <ReceiptIcon className="h-4 w-4 text-brand" />
                  <span>Gerar Recibo PIX</span>
                </button>
              )}

              {finalPrice > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const tempWo: WorkOrder = {
                      id: currentWorkOrderId || generateUuid(),
                      userId: 'current_user',
                      nicheKey: selectedNiche,
                      orderNumber,
                      title,
                      clientName,
                      clientPhone,
                      clientAddress,
                      clientDocument,
                      scheduledStart,
                      scheduledEnd,
                      status: orderStatus,
                      technicalReport,
                      notes,
                      checklistData: checklists,
                      itemsBreakdown: scopeItems,
                      totalLabor,
                      totalMaterials,
                      finalPrice,
                      paymentTerms,
                      photos,
                      signatures,
                      vehicleData,
                      hvacData,
                      beautyData,
                      createdAt: new Date().toISOString(),
                      updatedAt: new Date().toISOString()
                    };
                    setSelectedWoForNfse(tempWo);
                  }}
                  className="flex-1 sm:flex-initial px-5 py-3 bg-amber-500/10 hover:bg-amber-500/20 text-amber-950 font-bold text-xs rounded-xl border border-amber-300 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                  title="Emitir Nota Fiscal de Serviço Nacional (NFS-e)"
                >
                  <Zap className="h-4 w-4 text-amber-600" />
                  <span>Emitir NFS-e</span>
                </button>
              )}

              <button
                type="button"
                onClick={handleSaveOrder}
                disabled={isSaving}
                className="flex-1 sm:flex-initial px-7 py-3 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm shadow-brand/20 flex items-center justify-center gap-2 cursor-pointer active:scale-95 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span>{isSaving ? 'Salvando...' : 'Salvar Ordem de Serviço'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ABA 2: HISTÓRICO DE ORDENS DE SERVIÇO ─── */}
      {activeTab === 'history' && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 space-y-4">
          {/* Métricas de OS */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Faturado em Concluídas</span>
              <span className="text-base font-black text-brand">{formatCurrencyBRL(totalCompletedAmount)}</span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">OSs Abertas / Em Curso</span>
              <span className="text-base font-black text-amber-700">{totalOpenCount} ordens</span>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-zinc-100 shadow-sm col-span-2 sm:col-span-1">
              <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total de OSs Cadastradas</span>
              <span className="text-base font-black text-zinc-900">{workOrders.length} ordens</span>
            </div>
          </div>

          {/* Filtros & Busca */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Buscar por número, cliente, placa ou serviço..."
                value={historySearch}
                onChange={e => setHistorySearch(e.target.value)}
                className="w-full bg-white border border-zinc-100 rounded-2xl pl-10 pr-4 py-3 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20 shadow-xs"
              />
            </div>

            <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
              {[
                { status: 'all', label: 'Todas' },
                { status: 'open', label: 'Abertas' },
                { status: 'in_progress', label: 'Em Curso' },
                { status: 'completed', label: 'Concluídas' },
                { status: 'canceled', label: 'Canceladas' }
              ].map(st => (
                <button
                  key={st.status}
                  type="button"
                  onClick={() => setHistoryStatusFilter(st.status as any)}
                  className={`px-3 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    historyStatusFilter === st.status
                      ? 'bg-zinc-900 text-white font-black shadow-xs'
                      : 'bg-white text-zinc-600 border border-zinc-100 hover:bg-zinc-50'
                  }`}
                >
                  {st.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lista de Cards de OS */}
          {filteredWorkOrders.length === 0 ? (
            <div className="py-12 bg-white border border-dashed border-zinc-200 rounded-3xl text-center p-6 flex flex-col items-center justify-center">
              <ClipboardList className="h-10 w-10 text-zinc-300 mb-2" />
              <p className="text-xs font-bold text-zinc-700">Nenhuma Ordem de Serviço encontrada.</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Crie sua primeira OS profissional tocando em "Nova OS".</p>
              <button
                type="button"
                onClick={handleResetForm}
                className="mt-3 px-4 py-2 bg-brand text-white text-xs font-bold rounded-xl active:scale-95 transition-all cursor-pointer"
              >
                Criar Primeira OS
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredWorkOrders.map(wo => {
                const dateStr = new Date(wo.createdAt || Date.now()).toLocaleDateString('pt-BR');
                const statusBadge = {
                  open: { label: 'Aberta', class: 'bg-blue-50 text-blue-700 border-blue-200' },
                  in_progress: { label: 'Em Execução', class: 'bg-amber-50 text-amber-700 border-amber-200' },
                  completed: { label: 'Concluída', class: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
                  canceled: { label: 'Cancelada', class: 'bg-zinc-100 text-zinc-600 border-zinc-200' }
                }[wo.status || 'open'];

                const photoCount = wo.photos?.length || 0;
                const sigCount = wo.signatures?.length || 0;
                const checkedCount = wo.checklistData?.filter(c => c.checked).length || 0;
                const totalCheckCount = wo.checklistData?.length || 0;

                return (
                  <div
                    key={wo.id}
                    className="bg-white border border-zinc-100 hover:border-zinc-200 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                  >
                    <div className="space-y-2 min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-black text-xs bg-zinc-100 text-zinc-800 px-2 py-0.5 rounded-md border border-zinc-200">
                          {wo.orderNumber}
                        </span>
                        <span className={`text-[10px] font-black uppercase border px-2 py-0.5 rounded-full ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-[11px] text-zinc-400 font-medium flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {dateStr}
                        </span>
                      </div>

                      <div>
                        <h3 className="text-sm font-black text-zinc-900 truncate">{wo.title}</h3>
                        <div className="flex items-center gap-2 text-xs text-zinc-600 font-medium mt-0.5">
                          <User className="h-3.5 w-3.5 text-brand shrink-0" />
                          <span className="font-bold text-zinc-800">{wo.clientName}</span>
                          {wo.clientPhone && <span className="text-zinc-400 font-mono">({wo.clientPhone})</span>}
                        </div>
                      </div>

                      {/* Metadados específicos (Placa / Ar-condicionado) */}
                      {wo.vehicleData && (
                        <div className="flex items-center gap-1.5 text-[11px] bg-zinc-50 border border-zinc-100 rounded-lg px-2.5 py-1 text-zinc-700 w-fit">
                          <Car className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="font-bold">{wo.vehicleData.brand} {wo.vehicleData.model}</span>
                          <span className="font-mono font-black text-zinc-900 bg-white px-1.5 py-0.2 rounded border border-zinc-200 text-[10px]">
                            {wo.vehicleData.plate}
                          </span>
                        </div>
                      )}

                      {wo.hvacData && (
                        <div className="flex items-center gap-1.5 text-[11px] bg-zinc-50 border border-zinc-100 rounded-lg px-2.5 py-1 text-zinc-700 w-fit">
                          <Wind className="h-3.5 w-3.5 text-blue-600" />
                          <span className="font-bold">{wo.hvacData.brand} {wo.hvacData.modelName}</span>
                          <span className="text-zinc-500 font-medium">({wo.hvacData.btus} BTUs • {wo.hvacData.gasType})</span>
                        </div>
                      )}

                      {wo.deviceData && (
                        <div className="flex items-center gap-1.5 text-[11px] bg-zinc-50 border border-zinc-100 rounded-lg px-2.5 py-1 text-zinc-700 w-fit">
                          <Smartphone className="h-3.5 w-3.5 text-brand" />
                          <span className="font-bold">{wo.deviceData.brand} {wo.deviceData.model}</span>
                          <span className="text-zinc-500 font-medium truncate max-w-[120px]">SN: {wo.deviceData.imeiOrSerial || 'N/A'}</span>
                        </div>
                      )}

                      {/* Indicadores de Vistoria, Fotos, Assinatura e Preço */}
                      <div className="flex items-center gap-3 text-xs pt-1 flex-wrap">
                        <span className="font-black text-emerald-700 text-sm">
                          {formatCurrencyBRL(wo.finalPrice || 0)}
                        </span>

                        {totalCheckCount > 0 && (
                          <span className="text-[10px] font-bold text-zinc-500 bg-zinc-50 px-2 py-0.5 rounded-md border border-zinc-100">
                            Checklist: {checkedCount}/{totalCheckCount}
                          </span>
                        )}

                        {photoCount > 0 && (
                          <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100 flex items-center gap-1">
                            <Camera className="h-3 w-3" /> {photoCount} fotos
                          </span>
                        )}

                        {sigCount > 0 && (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3" /> Assinada
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex items-center gap-1.5 shrink-0 w-full sm:w-auto justify-end border-t sm:border-t-0 pt-2 sm:pt-0 border-zinc-100">
                      <button
                        type="button"
                        onClick={() => handleDirectWhatsAppShare(wo)}
                        className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                        title="Enviar resumo rápido pelo WhatsApp"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPreviewWorkOrder(wo)}
                        className="px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                        title="Visualizar e Baixar PDF da OS"
                      >
                        <Printer className="h-3.5 w-3.5 text-zinc-600" />
                        <span>PDF</span>
                      </button>

                      {onOpenReceipt && (
                        <button
                          type="button"
                          onClick={() => onOpenReceipt(wo)}
                          className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-brand border border-emerald-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                          title="Gerar Recibo PIX"
                        >
                          <ReceiptIcon className="h-3.5 w-3.5" />
                          <span>Recibo</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setSelectedWoForNfse(wo)}
                        className="px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-950 border border-amber-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer active:scale-95"
                        title="Emitir Nota Fiscal de Serviço Nacional (NFS-e)"
                      >
                        <Zap className="h-3.5 w-3.5 text-amber-600" />
                        <span>NFS-e</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleSelectForEdit(wo)}
                        className="px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl text-xs font-bold transition-all cursor-pointer active:scale-95"
                        title="Editar / Reabrir"
                      >
                        <span>Editar</span>
                      </button>

                      {onDeleteWorkOrder && canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDeleteWorkOrderAction(wo)}
                          className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all cursor-pointer active:scale-95"
                          title="Excluir OS"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal de Assinatura */}
      <TouchSignatureModal
        isOpen={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        documentId={orderNumber}
        documentType="work_order_delivery"
        documentTitle={`${orderNumber} - ${title}`}
        providerName={providerName}
        clientName={clientName || 'Cliente'}
        clientDocument={clientDocument}
        totalValue={finalPrice}
        onSaveSignature={handleSaveSignature}
      />

      {/* Modal de Prévia e Download do PDF da OS */}
      <WorkOrderPdfPreviewModal
        isOpen={Boolean(previewWorkOrder)}
        onClose={() => setPreviewWorkOrder(null)}
        workOrder={previewWorkOrder || {}}
        user={{
          businessName: providerName,
          ownerName: providerName
        }}
      />

      {/* Modal de Emissão de NFS-e Nacional (Receita Federal / Padrão ADN) */}
      <NfseEmissionModal
        isOpen={Boolean(selectedWoForNfse)}
        onClose={() => setSelectedWoForNfse(null)}
        workOrder={selectedWoForNfse}
        onSuccess={(result) => {
          console.log('[WorkOrderCreator] NFS-e autorizada:', result);
        }}
      />
    </div>
  );
}
